import { doc, getDoc, setDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';

const LANGUAGES = ['en', 'hi', 'mr', 'gu'];

export class GmImagesService {
  constructor(db, storage) {
    this.db = db;
    this.storage = storage;
  }

  async loadExistingImages(dateString) {
    try {
      console.log(`Loading GM images for date: ${dateString}`);
      
      const gmDoc = await getDoc(doc(this.db, 'gm_daily_images', dateString));
      
      if (!gmDoc.exists()) {
        console.log('No GM images document found for this date');
        return null;
      }

      const data = gmDoc.data();
      const result = {};

      for (const lang of LANGUAGES) {
        if (data[lang]) {
          result[lang] = {
            downloadURL: data[lang],
            storagePath: data[`${lang}_storagePath`] || `gm_ai_images/${dateString}/base_images/gm_${lang}_${dateString}.jpg`
          };
          console.log(`✓ Loaded ${lang.toUpperCase()} from Firestore`);
        }
      }

      return result;
    } catch (error) {
      console.error('Error loading GM images:', error);
      throw error;
    }
  }

  async uploadImage(lang, file, dateString) {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `gm_${lang}_${dateString}.${fileExtension}`;
      const storagePath = `gm_ai_images/${dateString}/base_images/${fileName}`;
      const storageRef = ref(this.storage, storagePath);

      await uploadBytes(storageRef, file);
      console.log(`✓ Uploaded ${lang} image successfully`);

      const downloadURL = await getDownloadURL(storageRef);
      console.log(`✓ Got download URL: ${downloadURL}`);
      return { downloadURL, storagePath };
    } catch (error) {
      console.error(`✗ Failed to upload ${lang} image:`, error);
      throw new Error(`Failed to upload ${lang.toUpperCase()} image: ${error.message}`);
    }
  }

  async uploadFromDataURL(lang, dataUrl, dateString) {
    try {
      const fileName = `gm_${lang}_${dateString}.jpg`;
      const storagePath = `gm_ai_images/${dateString}/base_images/${fileName}`;
      const storageRef = ref(this.storage, storagePath);

      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      await uploadBytes(storageRef, blob);
      console.log(`✓ Uploaded localStorage ${lang} image successfully`);

      const downloadURL = await getDownloadURL(storageRef);
      console.log(`✓ Got download URL: ${downloadURL}`);
      return { downloadURL, storagePath };
    } catch (error) {
      console.error(`✗ Failed to upload localStorage ${lang} image:`, error);
      throw new Error(`Failed to upload saved ${lang.toUpperCase()} image: ${error.message}`);
    }
  }

  async deleteImage(lang, storagePath, dateString) {
    try {
      let deleted = false;
      if (storagePath) {
        try {
          const imageRef = ref(this.storage, storagePath);
          await deleteObject(imageRef);
          deleted = true;
          console.log(`✓ Deleted ${lang.toUpperCase()} image from storage: ${storagePath}`);
        } catch (error) {
          if (error.code !== 'storage/object-not-found') {
            throw error;
          }
          console.log(`Storage object already gone for ${lang}: ${storagePath}`);
        }
      }

      // Only sweep when the recorded path did not do the job. It used to run
      // unconditionally, guessing five extensions per language — so a clean
      // delete of gm_en.png was still followed by 404s for .jpg, .jpeg, .gif
      // and .webp, four per language, sixteen per clear. The guesses only ever
      // mattered when storagePath was missing or stale.
      if (deleted) return;

      const extensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      for (const ext of extensions) {
        try {
          const imagePath = `gm_ai_images/${dateString}/base_images/gm_${lang}_${dateString}.${ext}`;
          if (storagePath === imagePath) continue;
          await deleteObject(ref(this.storage, imagePath));
          console.log(`✓ Deleted ${lang.toUpperCase()} image from storage (${ext})`);
          break;
        } catch (error) {
          if (error.code !== 'storage/object-not-found') {
            throw error;
          }
        }
      }
    } catch (error) {
      if (error.code !== 'storage/object-not-found') {
        console.error(`Error deleting ${lang} image:`, error);
        throw error;
      }
    }
  }

  async saveToFirestore(dateString, languageData) {
    try {
      const gmDocData = {
        date: dateString,
        generatedAt: serverTimestamp(),
        model: 'web_upload'
      };

      let savedCount = 0;
      for (const lang of LANGUAGES) {
        if (languageData[lang]) {
          gmDocData[lang] = languageData[lang].downloadURL;
          gmDocData[`${lang}_variant`] = 'manual_upload';
          gmDocData[`${lang}_dayTheme`] = 'custom';
          if (languageData[lang].storagePath) {
            gmDocData[`${lang}_storagePath`] = languageData[lang].storagePath;
          }
          savedCount++;
        }
      }

      if (savedCount > 0) {
        await setDoc(doc(this.db, 'gm_daily_images', dateString), gmDocData, { merge: true });
        console.log(`✓ Saved ${savedCount} GM images to Firestore`);
        return savedCount;
      }

      return 0;
    } catch (error) {
      console.error('Error saving to Firestore:', error);
      throw error;
    }
  }

  async removeFromFirestore(lang, dateString) {
    try {
      const docRef = doc(this.db, 'gm_daily_images', dateString);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        console.log(`No gm_daily_images/${dateString} doc — nothing to remove for ${lang}`);
        return;
      }

      const updateData = {};
      updateData[lang] = deleteField();
      updateData[`${lang}_variant`] = deleteField();
      updateData[`${lang}_dayTheme`] = deleteField();
      updateData[`${lang}_storagePath`] = deleteField();

      await updateDoc(docRef, updateData);
      console.log(`✓ Removed ${lang.toUpperCase()} from Firestore gm_daily_images/${dateString}`);
    } catch (error) {
      // Doc missing or field already gone — treat as cleared
      if (error.code === 'not-found' || /No document to update/i.test(error.message || '')) {
        console.warn(`gm_daily_images doc missing while removing ${lang}:`, error.message);
        return;
      }
      console.warn(`Could not remove ${lang} from Firestore:`, error.message);
      throw error;
    }
  }

}
